import React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
  CardGiftcard as GiftIcon,
  Book as BookIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../../../theme";
import { useNavigate } from "react-router-dom";
import patterbackground from "../images/pattern.jpeg";

interface ResourceLink {
  id: string;
  title: string;
  description: string;
  icon: "download" | "gift" | "book";
  url: string;
}

interface ThankYouModalProps {
  open: boolean;
  onClose: () => void;
  resources?: ResourceLink[];
  showCheckIcon?: boolean;
  referenceNumber?: string;
  quoteId?: number | null;
  belovedName?: string;
}

const DEFAULT_RESOURCES: ResourceLink[] = [
  {
    id: "memorial",
    title: "Free Memorial Page",
    description: "Create a Beautiful Tribute",
    icon: "book",
    url: "/client-portal",
  },
  {
    id: "guide",
    title: "Download Free Guide",
    description: "Complete Guide to Funerals in Harare",
    icon: "download",
    url: "#",
  },
  {
    id: "voucher",
    title: "Get $50 Discount Voucher",
    description: "For Funeral Programs Printing",
    icon: "gift",
    url: "#",
  },
];

const ThankYouModal: React.FC<ThankYouModalProps> = ({
  open,
  onClose,
  resources = DEFAULT_RESOURCES,
  showCheckIcon = true,
  referenceNumber,
  quoteId,
  belovedName,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const memorialUrl = belovedName
    ? (() => {
        const params = new URLSearchParams({
          belovedName: belovedName,
        });
        return `https://client.muzukuru.com/?${params.toString()}`;
      })()
    : "#";

  const handleResourceClick = (url: string) => {
    if (url && url !== "#") {
      window.open(url, "_blank");
    }
  };

  const getIcon = (iconType: "download" | "gift" | "book") => {
    return iconType === "download"
      ? DownloadIcon
      : iconType === "gift"
        ? GiftIcon
        : BookIcon;
  };

  const padding = isMobile ? 2 : 2;
  const iconSize = isMobile ? 48 : 60;
  const titleSize = isMobile ? 20 : 24;
  const buttonPadding = isMobile ? 1.5 : 2;
  const gap = isMobile ? 1.5 : 2;
  const mb = isMobile ? 2 : 2;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          margin: isMobile ? 1 : 2,
          maxHeight: isMobile ? "95vh" : "90vh",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          p: 3,
          bgcolor: "#614338",
          color: "white",
          overflow: "hidden",
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundImage: `url(${patterbackground})`,
            backgroundRepeat: "repeat",
            backgroundSize: "contain",
            opacity: 0.1,
            zIndex: 0,
          }}
        />
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: isMobile ? 6 : 8,
            top: isMobile ? 6 : 8,
            color: "white",
            padding: "4px",
          }}
        >
          <CloseIcon />
        </IconButton>

        {showCheckIcon && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: isMobile ? 1.5 : 2,
            }}
          >
            <CheckCircleIcon sx={{ fontSize: iconSize, color: "white" }} />
          </Box>
        )}

        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ mb: 1, fontSize: titleSize }}
        >
          Thank You!
        </Typography>
        <Typography
          variant={isMobile ? "subtitle1" : "body1"}
          fontWeight="bold"
          sx={{
            fontSize: "1.125rem",
            lineHeight: 1.4,
            color: "white",
          }}
        >
          Your quote request has been submitted successfully!
        </Typography>

        <Typography
          variant="body1"
          sx={{
            opacity: 0.8,
            fontSize: "0.9375rem",
            lineHeight: 1.4,
            maxWidth: "480px",
            mx: "auto",
            color: colors.primary[500],
          }}
        >
          Your Reference Number is: <strong>{referenceNumber}</strong>
        </Typography>
      </Box>

      <DialogContent sx={{ p: padding }}>
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          sx={{
            mb,
            textAlign: "center",
            fontSize: isMobile ? "0.9375rem" : undefined,
          }}
        >
          While you wait, here are some helpful resources
        </Typography>

        {/* <Button
          fullWidth
          variant="contained"
          onClick={() => {
            navigate(`/quote/${quoteId}`);
            onClose();
          }}
          sx={{
            py: 1.5,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            mb,
            background: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[400]} 100%)`,
            color: "white",
            // mb: 2,
            // "&:hover": {
            //   bgcolor: colors.primary[500],
            // },
          }}
        >
          View Quote Details
        </Button> */}

        <Box sx={{ display: "flex", flexDirection: "column", gap, mb }}>
          {resources.map((resource) => {
            const Icon = getIcon(resource.icon);

            // If this is the memorial resource, use memorialUrl
            const url = resource.id === "memorial" ? memorialUrl : resource.url;
            const isDisabled = !url || url === "#";

            return (
              <Button
                key={resource.id}
                onClick={() => !isDisabled && handleResourceClick(url)}
                disabled={isDisabled}
                sx={{
                  background: isDisabled
                    ? "rgba(255,255,255,0.1)"
                    : colors.primary[400],
                  color: isDisabled ? "rgba(255,255,255,0.5)" : "white",
                  border: isDisabled ? "1px solid gray" : "none",
                  p: buttonPadding,
                  borderRadius: 2,
                  textTransform: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  textAlign: "left",
                  minHeight: isMobile ? 60 : 72,
                  cursor: isDisabled ? "not-allowed" : "pointer",
                  "&:hover": {
                    background: isDisabled
                      ? "rgba(255,255,255,0.1)"
                      : "#997466",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: isMobile ? 1.5 : 2,
                  }}
                >
                  <Box
                    sx={{
                      width: isMobile ? 40 : 48,
                      height: isMobile ? 40 : 48,
                      borderRadius: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      opacity: isDisabled ? 0.5 : 1,
                    }}
                  >
                    <Icon sx={{ fontSize: isMobile ? 20 : 24 }} />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: isMobile ? "0.875rem" : "1rem",
                        mb: isMobile ? 0.25 : 0.5,
                        opacity: isDisabled ? 0.5 : 1,
                      }}
                    >
                      {resource.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: isMobile ? "0.75rem" : "0.875rem",
                        opacity: isDisabled ? 0.5 : 0.9,
                      }}
                    >
                      {resource.description}
                    </Typography>
                  </Box>
                </Box>
                <ArrowForwardIcon
                  sx={{
                    fontSize: isMobile ? 18 : 20,
                    flexShrink: 0,
                    opacity: isDisabled ? 0.5 : 1,
                  }}
                />
              </Button>
            );
          })}
        </Box>

        <Box
          sx={{
            bgcolor: "grey.50",
            borderRadius: 2,
            p: isMobile ? 2 : 2.5,
            mb: isMobile ? 2 : 3,
            textAlign: "center",
            border: "1px solid",
            borderColor: "grey.200",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              lineHeight: 1.5,
              fontSize: isMobile ? "0.8125rem" : undefined,
            }}
          >
            We appreciate your trust in Muzukuru Funeral Streaming. Our team
            will contact you shortly via WhatsApp with your personalised quote.{" "}
            <Box
              component="span"
              onClick={() => {
                if (quoteId) {
                  navigate(`/quote/${quoteId}`);
                  onClose();
                }
              }}
              sx={{
                color: "primary.main",
                fontWeight: 600,
                cursor: "pointer",
                textDecoration: "underline",
                "&:hover": {
                  color: "primary.dark",
                },
              }}
            >
              View Quotation Details
            </Box>
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ThankYouModal;
